import React from 'react';
import { useComposedRefs } from '@radix-ui/react-compose-refs';

import { LayoutRow } from '../../layout-row/LayoutRow';

import { ActionsMenuAsChildItem } from './ActionsMenuAsChildItem';

type Props<T extends { focus: () => void }> = {
  children: React.ReactElement<{ ref?: React.Ref<T> }>;
  containerClassName?: string;
  focusOnMouseEnter?: boolean;
};

export const ActionsMenuFocusableItem = <T extends { focus: () => void }>({
  children,
  containerClassName,
  focusOnMouseEnter = true,
}: Props<T>) => {
  const ref = React.useRef<T>(null);

  const handleMouseEnter = React.useCallback(() => {
    if (focusOnMouseEnter && ref.current) {
      ref.current.focus();
    }
  }, [focusOnMouseEnter]);

  if (React.Children.count(children) !== 1) {
    throw new Error('FocusableItem only works when children is a single React element.');
  }

  const child = React.Children.only(children) as React.ReactElement;
  const existingRef = (child as React.ReactElement & { ref?: React.Ref<T> }).ref;
  const composedRef = useComposedRefs<T>(ref, existingRef);

  const focusableElement = React.useMemo(
    () =>
      React.cloneElement(child, {
        ref: composedRef,
      }),
    [child, composedRef],
  );

  return (
    <div className={containerClassName} onMouseEnter={handleMouseEnter}>
      <LayoutRow width="100%" spacedChildren="sm" alignItems="center">
        <ActionsMenuAsChildItem>{focusableElement}</ActionsMenuAsChildItem>
      </LayoutRow>
    </div>
  );
};
